import type { FinCounterpartyRow } from '@shared/api';
import { Table } from '@shared/ui/Table';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconAlertTriangle, IconCheck } from '@shared/ui/icons';
import {
  formatDateTimeRu,
  COUNTERPARTY_TYPE_LABELS,
  COUNTERPARTY_SOURCE_LABELS,
} from '../financierFormat';
import {
  TableWrap,
  CenterTh,
  CenterTd,
  TableNoticeTd,
  CounterpartyName,
  CounterpartyFullName,
  CounterpartyUpdated,
  InnText,
  KppText,
  EmptyDash,
  BankNameText,
  BikText,
  BikWarning,
  MonoSpan,
  AccountWarning,
  VerifiedBadge,
} from './styled';

interface CounterpartyTableProps {
  counterparties: FinCounterpartyRow[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  onEditItem: (cp: FinCounterpartyRow) => void;
  canWrite: boolean;
}

export function CounterpartyTable({
  counterparties,
  isLoading,
  isError,
  refetch,
  onEditItem,
  canWrite,
}: CounterpartyTableProps) {
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <th>Наименование / Организация</th>
            <th>ИНН / КПП</th>
            <th>Тип</th>
            <th>Расчётный счёт (20 знаков)</th>
            <th>Банк и БИК</th>
            <th>Корр. счёт</th>
            <th>Источник</th>
            <th>Верификация</th>
            {canWrite && <CenterTh>Действия</CenterTh>}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Загрузка контрагентов…
              </TableNoticeTd>
            </tr>
          ) : isError ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Не удалось загрузить контрагентов.{' '}
                <Button $variant="secondary" $size="sm" onClick={() => refetch()}>
                  Повторить
                </Button>
              </TableNoticeTd>
            </tr>
          ) : counterparties.length === 0 ? (
            <tr>
              <TableNoticeTd colSpan={9}>
                Контрагенты не найдены
              </TableNoticeTd>
            </tr>
          ) : (
            counterparties.map((cp: FinCounterpartyRow) => {
              const typeLabel = COUNTERPARTY_TYPE_LABELS[cp.counterparty_type] ?? cp.counterparty_type;
              const sourceConf = COUNTERPARTY_SOURCE_LABELS[cp.source] ?? {
                label: cp.source,
                tone: 'neutral',
              };
              const hasValidAcc = cp.account_number && cp.account_number.length === 20;
              const hasValidBik = cp.bik && cp.bik.length === 9;

              return (
                <tr key={cp.id}>
                  <td>
                    <CounterpartyName>{cp.name}</CounterpartyName>
                    {cp.full_name && cp.full_name !== cp.name && (
                      <CounterpartyFullName>
                        {cp.full_name}
                      </CounterpartyFullName>
                    )}
                    <CounterpartyUpdated>
                      Обновлено: {formatDateTimeRu(cp.last_synced_at)}
                    </CounterpartyUpdated>
                  </td>
                  <td>
                    <InnText>
                      <MonoSpan>{cp.inn}</MonoSpan>
                    </InnText>
                    {cp.kpp && (
                      <KppText>
                        КПП: <MonoSpan>{cp.kpp}</MonoSpan>
                      </KppText>
                    )}
                  </td>
                  <td>
                    <Badge $tone="neutral">{typeLabel}</Badge>
                  </td>
                  <td>
                    {cp.account_number ? (
                      <div>
                        <MonoSpan>{cp.account_number}</MonoSpan>
                        {!hasValidAcc && (
                          <AccountWarning>
                            <IconAlertTriangle size={11} />
                            Не 20 цифр ({cp.account_number.length})
                          </AccountWarning>
                        )}
                      </div>
                    ) : (
                      <EmptyDash>—</EmptyDash>
                    )}
                  </td>
                  <td>
                    <BankNameText>
                      {cp.bank_name ?? '—'}
                    </BankNameText>
                    {cp.bik && (
                      <BikText>
                        БИК: <MonoSpan>{cp.bik}</MonoSpan>
                        {!hasValidBik && (
                          <BikWarning>
                            (не 9 цифр)
                          </BikWarning>
                        )}
                      </BikText>
                    )}
                  </td>
                  <td>
                    {cp.corr_account ? (
                      <MonoSpan>{cp.corr_account}</MonoSpan>
                    ) : (
                      <EmptyDash>—</EmptyDash>
                    )}
                  </td>
                  <td>
                    <Badge $tone={sourceConf.tone}>{sourceConf.label}</Badge>
                  </td>
                  <td>
                    <VerifiedBadge $verified={Boolean(cp.is_verified && hasValidAcc && hasValidBik)}>
                      {cp.is_verified && hasValidAcc && hasValidBik ? (
                        <>
                          <IconCheck size={12} />
                          1С проверен
                        </>
                      ) : (
                        'Требует уточнения'
                      )}
                    </VerifiedBadge>
                  </td>
                  {canWrite && (
                    <CenterTd>
                      <Button
                        $variant="ghost"
                        $size="sm"
                        onClick={() => onEditItem(cp)}
                      >
                        Редактировать
                      </Button>
                    </CenterTd>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
}
